/**
 * Google Taxonomy Search Component
 *
 * Provides a search interface for finding and selecting categories from
 * the Google Product Taxonomy. Features debounced search, full path display,
 * and category selection.
 *
 * Date: 2025-11-20
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Search, Loader2, ChevronRight, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

export interface TaxonomyNode {
  id: number;
  name: string;
  path: string;
  level: number;
  parentId: number | null;
  children: TaxonomyNode[];
}

interface GoogleTaxonomySearchProps {
  onSelect: (node: TaxonomyNode) => void;
  initialValue?: TaxonomyNode;
  disabled?: boolean;
}

export function GoogleTaxonomySearch({
  onSelect,
  initialValue,
  disabled = false,
}: GoogleTaxonomySearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TaxonomyNode[]>([]);
  const [selectedNode, setSelectedNode] = useState<TaxonomyNode | undefined>(initialValue);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  // Sync selectedNode when initialValue changes
  useEffect(() => {
    setSelectedNode(initialValue);
  }, [initialValue]);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      await performSearch(query);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const performSearch = async (searchQuery: string) => {
    setIsSearching(true);
    setError(null);

    try {
      const response = await authFetch(
        `${API_URL}/google-taxonomy/search?q=${encodeURIComponent(searchQuery)}&limit=20`
      );

      if (!response.ok) {
        throw new Error('Failed to search taxonomy');
      }

      const data = await response.json();

      if (data.success) {
        setResults(data.data.results || []);
        setShowResults(true);
      } else {
        setError(data.error || 'Search failed');
      }
    } catch (err) {
      console.error('Taxonomy search error:', err);
      setError('Failed to search taxonomy. Please try again.');
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectNode = useCallback((node: TaxonomyNode) => {
    setSelectedNode(node);
    setQuery('');
    setShowResults(false);
    setResults([]);
    onSelect(node);
  }, [onSelect]);

  const handleClearSelection = useCallback(() => {
    setSelectedNode(undefined);
    setQuery('');
    setResults([]);
    setShowResults(false);
  }, []);

  const renderPathSegments = (path: string) => {
    const segments = path.split(' > ');
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {segments.map((segment, index) => (
          <React.Fragment key={index}>
            {index > 0 && <ChevronRight className="h-3 w-3 text-gray-400" />}
            <span className={index === segments.length - 1 ? 'font-semibold' : 'text-gray-600'}>
              {segment}
            </span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Selected Node Display */}
      {selectedNode && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="text-sm text-gray-600 mb-1">Selected Category</div>
                <div className="text-sm">{renderPathSegments(selectedNode.path)}</div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="secondary">ID: {selectedNode.id}</Badge>
                  <Badge variant="secondary">Level: {selectedNode.level}</Badge>
                </div>
              </div>
              {!disabled && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Input */}
      {!selectedNode && (
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search Google Product Taxonomy (e.g., 'motorcycle', 'cycling')..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={disabled}
              className="pl-10 pr-10"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
            )}
          </div>

          {/* Search Results Dropdown */}
          {showResults && results.length > 0 && (
            <Card className="absolute z-50 w-full mt-2 max-h-96 overflow-y-auto shadow-lg">
              <CardContent className="p-0">
                <div className="divide-y">
                  {results.map((node) => (
                    <button
                      key={node.id}
                      onClick={() => handleSelectNode(node)}
                      className="w-full text-left p-3 hover:bg-gray-50 transition-colors"
                      disabled={disabled}
                    >
                      <div className="text-sm">{renderPathSegments(node.path)}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          ID: {node.id}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          Level: {node.level}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Results */}
          {showResults && results.length === 0 && !isSearching && query.length >= 2 && (
            <Card className="absolute z-50 w-full mt-2 shadow-lg">
              <CardContent className="p-4">
                <div className="text-sm text-gray-600 text-center">
                  No categories found for "{query}"
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          {/* Helper Text */}
          {!query && !error && (
            <div className="mt-2 text-xs text-gray-500">
              Start typing to search Google Product Taxonomy (minimum 2 characters)
            </div>
          )}
        </div>
      )}
    </div>
  );
}
